import { useState } from "react";
import { useForm } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { motion, AnimatePresence } from "framer-motion";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { supabase } from "@/services/supabase";
import { toast } from "@/hooks/use-toast";
import { Loader2, Sparkles } from "lucide-react";

const waitlistSchema = z.object({
  fullName: z.string()
    .trim()
    .min(2, "Name must be at least 2 characters")
    .max(100, "Name must be less than 100 characters"),
  email: z.string()
    .trim()
    .email("Please enter a valid email address")
    .max(255, "Email must be less than 255 characters"),
  instagramHandle: z.string()
    .trim()
    .max(50, "Instagram handle must be less than 50 characters")
    .optional()
    .transform(val => {
      if (!val) return '';
      return val.startsWith('@') ? val : `@${val}`;
    })
});

type WaitlistFormData = z.infer<typeof waitlistSchema>;

interface WaitlistDialogProps {
  open: boolean;
  onOpenChange: (open: boolean) => void;
}

export default function WaitlistDialog({ open, onOpenChange }: WaitlistDialogProps) {
  const [isSubmitting, setIsSubmitting] = useState(false);

  const {
    register,
    handleSubmit,
    formState: { errors },
    reset,
  } = useForm<WaitlistFormData>({
    resolver: zodResolver(waitlistSchema),
  });

  const onSubmit = async (data: WaitlistFormData) => {
    setIsSubmitting(true);

    try {
      const { error } = await supabase
        .from('waitlist_signups')
        .insert([
          {
            full_name: data.fullName,
            email: data.email,
            instagram_handle: data.instagramHandle || null,
          },
        ]);

      if (error) {
        if (error.code === '23505') {
          toast({
            title: "Already on the list! 🎉",
            description: "This email is already registered for early access.",
            variant: "default",
          });
        } else {
          throw error;
        }
      } else {
        toast({
          title: "You're on the list! 🎉",
          description: "Check your email for confirmation and exclusive updates.",
          variant: "default",
        });
        reset();
        onOpenChange(false);
      }
    } catch (error) {
      toast({
        title: "Oops! Something went wrong",
        description: "Please try again or contact support.",
        variant: "destructive",
      });
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="sm:max-w-md bg-background/95 backdrop-blur-xl border-border/50">
        <DialogHeader>
          <DialogTitle className="flex items-center gap-2 text-2xl">
            <Sparkles className="w-6 h-6 text-primary" />
            Join the Waitlist
          </DialogTitle>
          <DialogDescription className="text-muted-foreground">
            Be the first to get early access and exclusive discount!
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="space-y-4 mt-4">
          <div className="space-y-2">
            <Label htmlFor="fullName" className="text-foreground">
              Full Name *
            </Label>
            <Input
              id="fullName"
              placeholder="John Doe"
              {...register("fullName")}
              className="bg-background/50 border-border/50 focus:border-primary"
              disabled={isSubmitting}
            />
            {errors.fullName && (
              <p className="text-sm text-destructive">{errors.fullName.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="email" className="text-foreground">
              Email Address *
            </Label>
            <Input
              id="email"
              type="email"
              placeholder="john@example.com"
              {...register("email")}
              className="bg-background/50 border-border/50 focus:border-primary"
              disabled={isSubmitting}
            />
            {errors.email && (
              <p className="text-sm text-destructive">{errors.email.message}</p>
            )}
          </div>

          <div className="space-y-2">
            <Label htmlFor="instagramHandle" className="text-foreground">
              Instagram Handle <span className="text-muted-foreground">(optional)</span>
            </Label>
            <Input
              id="instagramHandle"
              placeholder="@yourhandle"
              {...register("instagramHandle")}
              className="bg-background/50 border-border/50 focus:border-primary"
              disabled={isSubmitting}
            />
            {errors.instagramHandle && (
              <p className="text-sm text-destructive">{errors.instagramHandle.message}</p>
            )}
          </div>

          <Button
            type="submit"
            disabled={isSubmitting}
            className="w-full bg-primary hover:bg-primary/90 text-primary-foreground font-semibold py-6 text-base"
          >
            {isSubmitting ? (
              <>
                <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                Joining Waitlist...
              </>
            ) : (
              <>
                <Sparkles className="w-4 h-4 mr-2" />
                Join Waitlist
              </>
            )}
          </Button>

          <p className="text-xs text-center text-muted-foreground">
            We'll never share your information. Early access discount valid for waitlist members only.
          </p>
        </form>
      </DialogContent>
    </Dialog>
  );
}
